import React, { ReactElement, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search } from 'lucide-react';
import { Checkbox } from '@ui/components/ui/checkbox';

import { ProductSeries } from './productTime';

/** Not the shared Base UI popover: its portalled content scrolls the page to the top when opened. */
const ProductPicker = ({
  options,
  value,
  onChange,
  max,
}: {
  options: ProductSeries[];
  value: string[];
  onChange: (next: string[]) => void;
  max: number;
}): ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    // Focus the search straight away, but never scroll to do it — the whole point of this panel.
    searchRef.current?.focus({ preventScroll: true });

    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleOne = (key: string): void => {
    if (value.includes(key)) onChange(value.filter(k => k !== key));
    else if (value.length < max) onChange([...value, key]);
  };

  const needle = query.trim().toLowerCase();
  const shown = needle ? options.filter(s => s.label.toLowerCase().includes(needle)) : options;

  return (
    <div ref={ref} className="relative w-52">
      <button
        type="button"
        onClick={() => {
          setOpen(o => !o);
          setQuery('');
        }}
        className="flex h-8 w-full items-center justify-between gap-2 rounded-lg border border-input bg-popover px-3 text-left text-sm text-foreground shadow-xs outline-none transition-colors hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <span className="min-w-0 flex-1 truncate">{t('timeTrendCount', { count: value.length })}</span>
        <ChevronDown
          className={cx(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-150',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-full origin-top-right rounded-lg border border-border bg-popover p-1.5 text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-150 ease-out animate-in fade-in-0 zoom-in-95 slide-in-from-top-1">
          {/* Inset by 2px so the focus ring stays inside the panel instead of spilling over its border. */}
          <div className="relative mx-0.5 mb-1">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('search')}
              aria-label={t('search')}
              className="h-8 w-full rounded-md border border-input bg-transparent pl-7 pr-2 text-sm text-foreground outline-none transition-[color,box-shadow,border-color] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
            />
          </div>
          <p className="px-2 pb-1 pt-0.5 text-[11px] text-muted-foreground">{t('timeTrendMax', { max })}</p>
          {shown.length ? (
            <ul className="max-h-56 overflow-y-auto">
              {shown.map(s => {
                const checked = value.includes(s.key);
                const disabled = !checked && value.length >= max;

                return (
                  <li key={s.key}>
                    <button
                      type="button"
                      onClick={() => toggleOne(s.key)}
                      disabled={disabled}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Checkbox checked={checked} aria-hidden tabIndex={-1} />
                      <span className="min-w-0 flex-1 truncate">{s.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">{t('noMatches')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductPicker;
